/**
 * Module de gestion des notifications push pour QuizClasse
 */

import axios, { axiosWithCsrfRetry } from '@edu-quizz/shared/lib/axios';

class PushNotificationManager {
  constructor() {
    this.registration = null;
    this.subscription = null;
    this.publicKey = null;
  }

  /**
   * Vérifier si le navigateur supporte les notifications push
   */
  isSupported() {
    return 'serviceWorker' in navigator && 'PushManager' in window;
  }

  /**
   * Initialiser le gestionnaire de notifications
   * @param {ServiceWorkerRegistration} registration - Enregistrement du service worker
   */
  async init(registration) {
    if (!this.isSupported()) {
      console.warn('Push notifications are not supported in this browser');
      return false;
    }

    try {
      this.registration = registration;

      // Récupérer la clé publique VAPID
      await this.fetchPublicKey();

      // Vérifier si déjà abonné
      this.subscription = await this.registration.pushManager.getSubscription();

      return true;
    } catch (error) {
      console.error('Failed to initialize push notifications:', error);
      return false;
    }
  }

  /**
   * Récupérer la clé publique VAPID depuis le serveur
   */
  async fetchPublicKey() {
    try {
      const response = await axios.get('/api/push/public-key');
      this.publicKey = response.data.public_key;
      return this.publicKey;
    } catch (error) {
      console.error('Failed to fetch VAPID public key:', error);
      throw error;
    }
  }

  /**
   * Convertir la clé publique en format Uint8Array
   */
  urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding)
      .replace(/\-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }

  /**
   * Demander la permission pour les notifications
   */
  async requestPermission() {
    const permission = await Notification.requestPermission();

    if (permission === 'granted') {
      console.log('Notification permission granted');
      return true;
    } else if (permission === 'denied') {
      console.warn('Notification permission denied');
      return false;
    } else {
      console.log('Notification permission dismissed');
      return false;
    }
  }

  /**
   * S'abonner aux notifications push
   */
  async subscribe() {
    if (!this.registration) {
      console.error('Service Worker not registered');
      return false;
    }

    if (!this.publicKey) {
      await this.fetchPublicKey();
    }

    try {
      // Demander la permission si nécessaire
      if (Notification.permission !== 'granted') {
        const granted = await this.requestPermission();
        if (!granted) {
          return false;
        }
      }

      // S'abonner aux notifications push
      const subscription = await this.registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: this.urlBase64ToUint8Array(this.publicKey),
      });

      this.subscription = subscription;

      // Envoyer l'abonnement au serveur
      await this.sendSubscriptionToServer(subscription);

      console.log('Successfully subscribed to push notifications');
      return true;
    } catch (error) {
      console.error('Failed to subscribe to push notifications:', error);
      return false;
    }
  }

  /**
   * Se désabonner des notifications push
   */
  async unsubscribe() {
    if (!this.subscription) {
      console.log('No active subscription');
      return true;
    }

    try {
      // Se désabonner côté client
      await this.subscription.unsubscribe();

      // Informer le serveur
      await this.removeSubscriptionFromServer(this.subscription);

      this.subscription = null;

      console.log('Successfully unsubscribed from push notifications');
      return true;
    } catch (error) {
      console.error('Failed to unsubscribe from push notifications:', error);
      return false;
    }
  }

  /**
   * Envoyer l'abonnement au serveur
   */
  async sendSubscriptionToServer(subscription) {
    const subscriptionJson = subscription.toJSON();

    const response = await axiosWithCsrfRetry(() =>
      axios.post('/api/push/subscribe', {
        endpoint: subscriptionJson.endpoint,
        keys: subscriptionJson.keys,
      })
    );

    return response.data;
  }

  /**
   * Supprimer l'abonnement du serveur
   */
  async removeSubscriptionFromServer(subscription) {
    const subscriptionJson = subscription.toJSON();

    const response = await axiosWithCsrfRetry(() =>
      axios.post('/api/push/unsubscribe', {
        endpoint: subscriptionJson.endpoint,
      })
    );

    return response.data;
  }

  /**
   * Vérifier si l'utilisateur est abonné
   */
  async isSubscribed() {
    if (!this.registration) {
      return false;
    }

    const subscription = await this.registration.pushManager.getSubscription();
    return subscription !== null;
  }

  /**
   * Obtenir le statut de la permission
   */
  getPermissionStatus() {
    return Notification.permission;
  }
}

// Exporter une instance unique
export const pushNotifications = new PushNotificationManager();
